import {
  BasicInformationFormField,
  ExamScheduleFormField,
  LocationFormField,
  type ExamScheduleFormFieldProps,
  type LocationFormFieldProps,
} from '@exam-center/exam-event-creation';
import { OptionalInformationFormField } from '@exam-center/exam-event-management';
import {
  type ExamType,
  type RegistrationType,
  type RegistrationStatus,
  type ExamineeImportMethod,
} from '@exam-center/graphql';
import { Form, Paper, SectionTitle, Divider, Flex, FormButton } from '@shared/components';
import { type Prettify } from '@shared/utils';
import { FormProvider, type UseFormReturn } from 'react-hook-form';

import { type ExamEventTemporarilyClosedFormType } from '../../containers/exam-event-temporarily-closed-logic/schema';
import {
  ExamCoursesReadonlyField,
  type ExamCoursesReadonlyFieldProps,
} from '../exam-courses-readonly-field';
import { ExamEventStatusFormField } from '../exam-event-status-form-field';

interface ExamEventTemporarilyClosedFormProps {
  form: UseFormReturn<ExamEventTemporarilyClosedFormType>;
  onSubmit: (data: ExamEventTemporarilyClosedFormType) => void;
  slotProps: Prettify<
    ExamScheduleFormFieldProps['slotProps'] &
      LocationFormFieldProps['slotProps'] & {
        examEventName?: string;
        examType?: ExamType;
        registrationType?: RegistrationType;
        examineeImportMethod?: ExamineeImportMethod;
        examEventStatusOptions?: RegistrationStatus[];
        reason?: string;
        examCourses?: ExamCoursesReadonlyFieldProps;
      }
  >;
}

export default function ExamEventTemporarilyClosedForm(
  props: Readonly<ExamEventTemporarilyClosedFormProps>,
) {
  const { form, onSubmit, slotProps } = props;

  return (
    <FormProvider {...form}>
      <Form onSubmit={form.handleSubmit(onSubmit)}>
        <Paper
          sx={(theme) => ({
            flex: 1,
            p: theme.spacing(6),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <SectionTitle title='Basic Information' />
          <BasicInformationFormField
            isNameEditable={false}
            examEventName={slotProps?.examEventName}
            isExamTypeEditable={false}
            examType={slotProps?.examType}
            isRegistrationTypeEditable={false}
            registrationType={slotProps?.registrationType}
            isExamineeImportMethodEditable={false}
            examineeImportMethod={slotProps?.examineeImportMethod}
          />
          <Divider />
          <SectionTitle title='Exam Courses' />
          <ExamCoursesReadonlyField
            courses={slotProps?.examCourses?.courses}
            isAllSelected={slotProps?.examCourses?.isAllSelected}
          />
          <Divider />
          <SectionTitle title='Exam Schedule' />
          <ExamScheduleFormField slotProps={slotProps} isExamDateEditable={false} />
          <Divider />
          <SectionTitle title='Exam Location' />
          <LocationFormField slotProps={slotProps} />
          <Divider />
          <SectionTitle title='Optional Information' />
          <OptionalInformationFormField /> <Divider />
          <SectionTitle title='Exam Event Status' />
          <ExamEventStatusFormField
            registrationStatusOptions={slotProps?.examEventStatusOptions ?? []}
            reason={slotProps?.reason}
          />
        </Paper>
        <Flex
          sx={(theme) => ({
            justifyContent: 'flex-end',
            gap: theme.spacing(3),
            pt: theme.spacing(6),
          })}
        >
          <FormButton size='large' label='บันทึก' />
        </Flex>
      </Form>
    </FormProvider>
  );
}
